import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  Fade,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MaintenanceRecord, MaintenanceStatus, MaintenanceType } from '../../types/maintenance';
import { getMaintenanceRecords } from '../../services/maintenanceService';
import { formatDate } from '../../utils/dateUtils';
import { vehicleService } from '../../services/vehicleService';

interface MaintenanceListProps {
  organizationId: string;
  selectedVehicle?: string | null;
  filters?: {
    vehicleId?: string;
    status?: MaintenanceStatus;
    type?: MaintenanceType;
    serviceProviderId?: string;
  };
  onEdit?: (maintenance: MaintenanceRecord) => void;
  onDelete?: (maintenance: MaintenanceRecord) => void;
}

const MaintenanceList: React.FC<MaintenanceListProps> = ({
  organizationId,
  selectedVehicle,
  filters = {},
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [vehiclesInfo, setVehiclesInfo] = useState<{[key: string]: { make: string; model: string; licensePlate: string }}>({});

  useEffect(() => {
    const fetchRecords = async () => {
      if (!organizationId) {
        setError(t('common.noOrganization'));
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Priorité au vehicleId des filtres, sinon utiliser selectedVehicle
        const effectiveVehicleId = filters?.vehicleId || selectedVehicle || undefined;

        const records = await getMaintenanceRecords(organizationId, {
          ...filters,
          vehicleId: effectiveVehicleId
        });
        
        // Charger les informations des véhicules
        const vehicleIds = [...new Set(records.map(record => record.vehicleId))];
        const vehiclesData: {[key: string]: { make: string; model: string; licensePlate: string }} = {};
        
        await Promise.all(vehicleIds.map(async (vehicleId) => {
          try {
            const vehicle = await vehicleService.getVehicle(vehicleId);
            if (vehicle) {
              vehiclesData[vehicleId] = {
                make: vehicle.make,
                model: vehicle.model,
                licensePlate: vehicle.licensePlate
              };
            }
          } catch (error) {
            console.error(`Error fetching vehicle ${vehicleId}:`, error);
          }
        }));

        setVehiclesInfo(vehiclesData);
        setMaintenanceRecords(records);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching maintenance records:', err);
        if (err instanceof Error && err.message === 'Insufficient permissions to access maintenance records') {
          setError(t('maintenance.errors.insufficientPermissions'));
        } else {
          setError(t('maintenance.errors.fetchFailed'));
        }
        setMaintenanceRecords([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecords();
  }, [organizationId, t, selectedVehicle, filters]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: MaintenanceStatus) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!organizationId) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>{t('common.noOrganization')}</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.reload()}
          >
            {t('common.retry')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={300}>
      <Box>
        {maintenanceRecords.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography>{t('maintenance.noRecords')}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('maintenance.openDate')}</TableCell>
                    <TableCell>{t('maintenance.table.headers.vehicle')}</TableCell>
                    <TableCell>{t('maintenance.form.fields.type')}</TableCell>
                    <TableCell>{t('maintenance.form.fields.status')}</TableCell>
                    <TableCell>{t('maintenance.form.fields.description')}</TableCell>
                    <TableCell>{t('maintenance.form.fields.costTTC')}</TableCell>
                    <TableCell>{t('maintenance.table.headers.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {maintenanceRecords
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((record) => (
                      <TableRow 
                        key={record.id}
                        sx={{
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <TableCell>{formatDate(record.openDate.toDate())}</TableCell>
                        <TableCell>
                          {vehiclesInfo[record.vehicleId] ? (
                            `${vehiclesInfo[record.vehicleId].make} ${vehiclesInfo[record.vehicleId].model} (${vehiclesInfo[record.vehicleId].licensePlate})`
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('common.loading')}...
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{t(`maintenance.types.${record.type}`)}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`maintenance.status.${record.status}`)} 
                            color={getStatusColor(record.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={record.description} placement="top">
                            <Typography>
                              {record.description.length > 100
                                ? `${record.description.substring(0, 100)}...`
                                : record.description}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{record.costTTC.toFixed(2)} €</TableCell>
                        <TableCell>
                          <Tooltip title={t('maintenance.table.tooltips.details')}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/maintenance/${record.id}`)}
                              aria-label={t('maintenance.table.tooltips.details')}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {onEdit && (
                            <Tooltip title={t('maintenance.table.tooltips.edit')}>
                              <IconButton
                                size="small"
                                onClick={() => onEdit(record)}
                                aria-label={t('common.edit')}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onDelete && (
                            <Tooltip title={t('maintenance.table.tooltips.delete')}>
                              <IconButton
                                size="small"
                                onClick={() => onDelete(record)}
                                aria-label={t('common.delete')}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={maintenanceRecords.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('common.rowsPerPage')}
              />
            </TableContainer>
          </>
        )}
      </Box>
    </Fade>
  );
};

export default MaintenanceList;
